import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { TrophyIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useSeasonPointsStore } from '../../state/seasonPointsStore';

function formatTimeRemaining(endDate: string): string {
  const now = new Date();
  const end = new Date(endDate);
  const diff = end.getTime() - now.getTime();
  if (diff <= 0) return 'Ended';
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  if (days > 0) return `${days}d ${hours}h`;
  return `${hours}h`;
}

export const SeasonBanner: React.FC = () => {
  const { publicKey } = useWallet();
  const wallet = publicKey?.toBase58();
  const { currentSeason, myPoints, myRank, fetchCurrentSeason, fetchMyPoints } =
    useSeasonPointsStore();
  const [timeRemaining, setTimeRemaining] = useState('');

  useEffect(() => {
    fetchCurrentSeason();
  }, [fetchCurrentSeason]);

  useEffect(() => {
    if (currentSeason && wallet) {
      fetchMyPoints(wallet, currentSeason.id);
    }
  }, [currentSeason, wallet, fetchMyPoints]);

  useEffect(() => {
    if (!currentSeason) return;
    const update = () => setTimeRemaining(formatTimeRemaining(currentSeason.endDate));
    update();
    const interval = setInterval(update, 60000);
    return () => clearInterval(interval);
  }, [currentSeason]);

  if (!currentSeason) return null;

  const losses = myPoints ? myPoints.matchesPlayed - myPoints.matchesWon : 0;

  return (
    <div className="arcade-panel pixel-corners rounded-2xl p-4">
      {/* Header */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrophyIcon className="h-5 w-5 text-arcade-yellow" />
          <h2 className="font-display text-sm font-bold text-white">
            {currentSeason.name.toUpperCase()}
          </h2>
        </div>
        <div className="font-mono text-xs text-arcade-cyan">{timeRemaining}</div>
      </div>

      {/* Stats */}
      {wallet && myPoints ? (
        <div className="grid grid-cols-3 gap-3 font-pixel text-xs">
          <div>
            <div className="text-white/50">Rank</div>
            <div className="font-display text-lg text-white">#{myRank || '—'}</div>
          </div>
          <div>
            <div className="text-white/50">Points</div>
            <div className="font-display text-lg text-arcade-cyan">
              {Math.floor(myPoints.points).toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-white/50">Record</div>
            <div className="font-display text-lg">
              <span className="text-arcade-green">{myPoints.matchesWon}</span>
              <span className="text-white/30">-</span>
              <span className="text-arcade-pink">{losses}</span>
            </div>
          </div>
        </div>
      ) : (
        <p className="font-pixel text-xs text-white/50">Connect wallet to track your season</p>
      )}

      {/* Footer link */}
      <Link
        to="/seasons"
        className="mt-3 flex items-center justify-end gap-1 border-t border-white/10 pt-3 font-pixel text-xs text-arcade-cyan/70 transition-colors hover:text-arcade-cyan"
      >
        View Rewards
        <ChevronRightIcon className="h-3 w-3" />
      </Link>
    </div>
  );
};

// Stub kept for backward compatibility with RoomPage import
// Real season points are updated server-side via pointsService
export function updateSeasonRank(_won: boolean, _eloChange: number): void {
  // No-op: season data now comes from the server API
}
